import React, {useEffect} from 'react';
import {
  ActionModal,
  FormInput,
  useErrorHandler,
  ErrorModal,
  FileUploadDropzone,
  FilePreviewInformation,
} from '../../../../shared';
import {useAddDocumentForm} from '../../model';
import {useDocumentUpload} from '../../lib';
import styles from './AddDocumentModal.module.scss';

interface AddDocumentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  appealId: string;
}

export const AddFundraiserDocumentModal = ({open, setOpen, appealId}: AddDocumentModalProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    resetForm,
    submitForm,
    isValid,
    isLoading,
    submitError,
  } = useAddDocumentForm({appealId, setOpen});

  const {uploadId, fileName, fileSize, isUploading, uploadDocument, resetUpload} = useDocumentUpload();

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Документ',
    title: 'Ошибка при добавлении документа',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  useEffect(() => {
    if (open) {
      resetForm();
      resetUpload();
      setFieldValue('uploadId', '');
      setFieldValue('fileExtension', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Обновляем uploadId в форме когда файл загружен
  useEffect(() => {
    if (uploadId && fileName) {
      setFieldValue('uploadId', uploadId);

      // Определяем расширение файла из имени файла
      const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
      setFieldValue('fileExtension', fileExtension);

      setFieldTouched('uploadId', true, false);
      if (values.title) {
        setFieldTouched('title', true, false);
      }
    } else {
      // Если uploadId сброшен, очищаем поле в форме
      setFieldValue('uploadId', '');
    }
  }, [uploadId, fileName, setFieldValue, setFieldTouched, values.title, values.uploadId]);

  const handleRemoveFile = () => {
    resetUpload();
    setFieldValue('uploadId', '');
    setFieldValue('fileExtension', '');
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isUploading || isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Добавить документ'}
        textButtonApply={'Добавить'}
        textButtonCancel={'Отменить'}
        isDisabled={!isValid}>
        <FormInput
          label="Название документа"
          placeholder="Название документа"
          name="title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.title && errors.title}
        />

        <div className={styles.fileUploadSection}>
          {!uploadId && !values.uploadId ? (
            <FileUploadDropzone
              onFileSelect={uploadDocument}
              accept=".pdf,.jpg,.jpeg,.png"
              isLoading={isUploading}
              className={styles.dropzone}
            />
          ) : (
            <FilePreviewInformation fileName={fileName} fileSize={fileSize} handleRemoveFile={handleRemoveFile} />
          )}
        </div>
      </ActionModal>

      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
